import React, { useState, useEffect } from 'react';
import { getMonthlySalesByStore } from '../api/api';
import { 
  Calendar, Building2, TrendingUp, DollarSign, CreditCard,
  Banknote, Phone, Receipt, Coins, ChevronLeft, ChevronRight,
  Download, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MonthlySalesReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('table'); // 'table' o 'chart'
  const [selectedStore, setSelectedStore] = useState('all');

  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const monthsFull = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  useEffect(() => {
    loadReport();
  }, [selectedYear]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await getMonthlySalesByStore(selectedYear);
      setReport(data);
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `Q${value?.toLocaleString() || '0'}`;
  };

  // Preparar datos para el gráfico
  const getChartData = () => {
    if (!report) return [];
    
    return months.map((month, index) => {
      const monthNum = index + 1;
      if (selectedStore === 'all') {
        return {
          name: month,
          Efectivo: report.global_months[monthNum]?.efectivo || 0,
          POS: report.global_months[monthNum]?.pos || 0,
          Recargas: report.global_months[monthNum]?.recargas || 0,
          Recaudo: report.global_months[monthNum]?.recaudo || 0,
          Sobrantes: report.global_months[monthNum]?.sobrantes || 0
        };
      } else {
        const store = report.stores.find(s => s.store_id === selectedStore);
        if (store) {
          return {
            name: month,
            Efectivo: store.months[monthNum]?.efectivo || 0,
            POS: store.months[monthNum]?.pos || 0,
            Recargas: store.months[monthNum]?.recargas || 0,
            Recaudo: store.months[monthNum]?.recaudo || 0,
            Sobrantes: store.months[monthNum]?.sobrantes || 0
          };
        }
      }
      return { name: month, Efectivo: 0, POS: 0, Recargas: 0, Recaudo: 0, Sobrantes: 0 };
    });
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-indigo-800 rounded-xl shadow-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 size={28} />
              Reporte de Ventas Mensuales por Tienda
            </h1>
            <p className="text-slate-300 mt-1">Desglose de ventas por mes, tienda y método de pago</p>
          </div>
          
          {/* Controles */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Selector de Tienda para gráfico */}
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
            >
              <option value="all">Todas las tiendas</option>
              {report?.stores?.map(store => (
                <option key={store.store_id} value={store.store_id}>{store.store_name}</option>
              ))}
            </select>

            {/* Selector de Año */}
            <div className="flex items-center space-x-2 bg-slate-700/50 rounded-lg px-3 py-2">
              <button 
                onClick={() => setSelectedYear(prev => prev - 1)} 
                className="p-1 hover:bg-slate-600 rounded"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="min-w-[80px] text-center font-bold text-lg">{selectedYear}</span>
              <button 
                onClick={() => setSelectedYear(prev => prev + 1)} 
                className="p-1 hover:bg-slate-600 rounded"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Toggle Vista */}
            <div className="flex bg-slate-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 rounded ${viewMode === 'table' ? 'bg-blue-500' : ''}`}
              >
                Tabla
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className={`px-3 py-1 rounded ${viewMode === 'chart' ? 'bg-blue-500' : ''}`}
              >
                Gráfico
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Resumen Totales Anuales */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-slate-600">
          <p className="text-xs text-slate-500 uppercase font-semibold">Total Anual</p>
          <p className="text-2xl font-bold text-slate-800">{formatCurrency(report?.global_totals?.total)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-emerald-500">
          <div className="flex items-center gap-1 text-xs text-slate-500 uppercase font-semibold">
            <Banknote size={14} /> Efectivo
          </div>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(report?.global_totals?.efectivo)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-1 text-xs text-slate-500 uppercase font-semibold">
            <CreditCard size={14} /> POS
          </div>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(report?.global_totals?.pos)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-1 text-xs text-slate-500 uppercase font-semibold">
            <Phone size={14} /> Recargas
          </div>
          <p className="text-2xl font-bold text-amber-600">{formatCurrency(report?.global_totals?.recargas)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-purple-500">
          <div className="flex items-center gap-1 text-xs text-slate-500 uppercase font-semibold">
            <Receipt size={14} /> Recaudo
          </div>
          <p className="text-2xl font-bold text-purple-600">{formatCurrency(report?.global_totals?.recaudo)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-orange-500">
          <div className="flex items-center gap-1 text-xs text-slate-500 uppercase font-semibold">
            <Coins size={14} /> Sobrantes
          </div>
          <p className="text-2xl font-bold text-orange-600">{formatCurrency(report?.global_totals?.sobrantes)}</p>
        </div>
      </div>

      {/* Gráfico */}
      {viewMode === 'chart' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">
            Ventas por Mes - {selectedStore === 'all' ? 'Todas las Tiendas' : report?.stores?.find(s => s.store_id === selectedStore)?.store_name}
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={getChartData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => `Q${value.toLocaleString()}`} />
              <Tooltip formatter={(value) => `Q${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="Efectivo" fill="#059669" stackId="a" />
              <Bar dataKey="POS" fill="#1e40af" stackId="a" />
              <Bar dataKey="Recargas" fill="#d97706" stackId="a" />
              <Bar dataKey="Recaudo" fill="#7c3aed" stackId="a" />
              <Bar dataKey="Sobrantes" fill="#ea580c" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabla Detallada por Tienda */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Building2 size={20} />
              Detalle por Tienda - {selectedYear}
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-600 uppercase sticky left-0 bg-slate-100 z-10">Tienda</th>
                  {months.map((month, i) => (
                    <th key={`month-${month}`} className="px-3 py-3 text-center text-xs font-bold text-slate-600 uppercase min-w-[100px]">
                      {month}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center text-xs font-bold text-slate-600 uppercase bg-slate-200 min-w-[100px]">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {report?.stores?.map((store, storeIndex) => (
                  <React.Fragment key={store.store_id}>
                    {/* Fila de Total por tienda */}
                    <tr className={`${storeIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50`}>
                      <td className="px-3 py-3 font-bold text-slate-800 sticky left-0 bg-inherit z-10">
                        {store.store_name}
                        <div className="text-xs font-normal text-slate-500">Total</div>
                      </td>
                      {months.map((_, i) => {
                        const monthData = store.months[i + 1];
                        return (
                          <td key={`total-${i}`} className="px-3 py-3 text-center font-semibold text-slate-800">
                            {formatCurrency(monthData?.total)}
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-center font-bold text-slate-900 bg-slate-200">
                        {formatCurrency(store.annual_totals?.total)}
                      </td>
                    </tr>
                    
                    {/* Sub-fila POS */}
                    <tr className="bg-blue-50/50 text-xs">
                      <td className="px-3 py-1 pl-6 text-blue-700 sticky left-0 bg-blue-50/50 z-10">
                        <div className="flex items-center gap-1"><CreditCard size={12} /> POS</div>
                      </td>
                      {months.map((_, i) => (
                        <td key={`cash-${i}`} className="px-3 py-1 text-center text-blue-600">
                          {formatCurrency(store.months[i + 1]?.pos)}
                        </td>
                      ))}
                      <td className="px-3 py-1 text-center font-semibold text-blue-700 bg-blue-100">
                        {formatCurrency(store.annual_totals?.pos)}
                      </td>
                    </tr>
                    
                    {/* Sub-fila Efectivo */}
                    <tr className="bg-emerald-50/50 text-xs">
                      <td className="px-3 py-1 pl-6 text-emerald-700 sticky left-0 bg-emerald-50/50 z-10">
                        <div className="flex items-center gap-1"><Banknote size={12} /> Efectivo</div>
                      </td>
                      {months.map((_, i) => (
                        <td key={`pos-${i}`} className="px-3 py-1 text-center text-emerald-600">
                          {formatCurrency(store.months[i + 1]?.efectivo)}
                        </td>
                      ))}
                      <td className="px-3 py-1 text-center font-semibold text-emerald-700 bg-emerald-100">
                        {formatCurrency(store.annual_totals?.efectivo)}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              
              {/* Footer - Totales Globales */}
              <tfoot>
                <tr className="bg-slate-800 text-white font-bold">
                  <td className="px-3 py-3 sticky left-0 bg-slate-800 z-10">TOTALES</td>
                  {months.map((_, i) => (
                    <td key={`grand-${i}`} className="px-3 py-3 text-center">
                      {formatCurrency(report?.global_months?.[i + 1]?.total)}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-center bg-slate-900">
                    {formatCurrency(report?.global_totals?.total)}
                  </td>
                </tr>
                <tr className="bg-blue-800 text-white text-xs">
                  <td className="px-3 py-2 sticky left-0 bg-blue-800 z-10">
                    <div className="flex items-center gap-1"><CreditCard size={12} /> POS Total</div>
                  </td>
                  {months.map((_, i) => (
                    <td key={`pct-${i}`} className="px-3 py-2 text-center">
                      {formatCurrency(report?.global_months?.[i + 1]?.pos)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center bg-blue-900 font-bold">
                    {formatCurrency(report?.global_totals?.pos)}
                  </td>
                </tr>
                <tr className="bg-emerald-800 text-white text-xs">
                  <td className="px-3 py-2 sticky left-0 bg-emerald-800 z-10">
                    <div className="flex items-center gap-1"><Banknote size={12} /> Efectivo Total</div>
                  </td>
                  {months.map((_, i) => (
                    <td key={`pct-${i}`} className="px-3 py-2 text-center">
                      {formatCurrency(report?.global_months?.[i + 1]?.efectivo)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center bg-emerald-900 font-bold">
                    {formatCurrency(report?.global_totals?.efectivo)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Leyenda */}
      <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
        <p className="font-semibold mb-2">Notas:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Total:</strong> Suma de todos los métodos de pago</li>
          <li><strong>POS:</strong> Pagos con tarjeta (va al banco)</li>
          <li><strong>Efectivo:</strong> Dinero en efectivo en caja</li>
          <li>Los datos se actualizan en tiempo real con cada venta registrada</li>
        </ul>
      </div>
    </div>
  );
};

export default MonthlySalesReport;
